import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faSearch, faTimes, faPlusCircle, faMinusCircle, faShoppingBag } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";

// App bits you already have
import Header from "../components/Header";
import AddToCartModal from "../elements/AddToCartModal";
import CategoryModal from "../elements/CategoryModal";
import { Context } from "../context/AppContext";
import { getOnlineStatusThunk } from "../store/restaurants";

// New drawer (React version)
import CartDrawer from "../elements/CartDrawer";

// ---------- Helpers ----------
function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function throttle(fn, wait = 120) {
  let last = 0;
  let timer;
  return (...args) => {
    const now = Date.now();
    if (now - last >= wait) {
      last = now;
      fn(...args);
    } else {
      clearTimeout(timer);
      timer = setTimeout(() => {
        last = Date.now();
        fn(...args);
      }, wait - (now - last));
    }
  };
}

// ---------- Component ----------
const OnlineOrder = ({ restaurant }) => {
  const dispatch = useDispatch();
  const { language, changedCart, setChangedCart, restaurantId, setRestaurantId } = useContext(Context);

  // From API (dynamic) – if not present, we’ll gracefully fallback to your example list
  const locationsFromApi = useSelector((s) => s?.restaurants?.locations || []);

  const restaurantStatus = useSelector((state) => state.restaurant.onlineStatus);

  // UI state
  const [activeCategory, setActiveCategory] = useState(0);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [addModal, setAddToCartModal] = useState(false);

  const [showChooseRestaurant, setShowChooseRestaurant] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedValue(searchValue, 250);

  const [currentCart, setCurrentCart] = useState([]);
  const [shake, setShake] = useState(false);

  const categoryListRef = useRef(null);

  // Ensure we prompt for a store first
  useEffect(() => {
    if (restaurantId === null || restaurantId === undefined) {
      setShowChooseRestaurant(true);
    }
  }, [restaurantId]);

  // Keep status up to date
  useEffect(() => {
    if (restaurantId) dispatch(getOnlineStatusThunk(restaurantId));
  }, [dispatch, restaurantId]);

  // Current cart sync (chain uses `cart${restaurantId}`)
  useEffect(() => {
    try {
      const items = JSON.parse(localStorage.getItem(`cart${restaurantId}`) || "[]");
      setCurrentCart(items);
    } catch {
      setCurrentCart([]);
    }
  }, [restaurantId, changedCart]);

  const calculateCartItems = useCallback(() => {
    return currentCart.reduce((total, item) => total + (Number(item?.quantity) || 0), 0);
  }, [currentCart]);

  useEffect(() => {
    const itemCount = calculateCartItems();
    if (itemCount > 0) {
      setShake(true);
      const timer = setTimeout(() => setShake(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [calculateCartItems]);

  const calculateCurrentCart = useCallback(() => {
    let subtotal = currentCart.reduce((total, it) => total + (Number(it?.Item?.price) || 0) * (Number(it?.quantity) || 1), 0);
    currentCart.forEach((it) => {
      (it?.options || []).forEach((opt) => {
        subtotal += (Number(opt?.priceModifier) || 0) * (Number(it?.quantity) || 1);
      });
    });
    return Number(subtotal.toFixed(2));
  }, [currentCart]);

  // Filter items by search (fast, simple)
  const filteredCategories = useMemo(() => {
    if (!restaurant?.Categories) return [];
    if (!debouncedSearch) return restaurant.Categories;

    const q = debouncedSearch.toLowerCase();
    return restaurant.Categories
      .map((cat) => ({
        ...cat,
        Items: (cat.Items || []).filter((i) => (i?.name || "").toLowerCase().includes(q)),
      }))
      .filter((cat) => (cat.Items || []).length > 0);
  }, [restaurant, debouncedSearch]);

  // Scroll spy
  useEffect(() => {
    const handleScrollThrottled = throttle(() => {
      const categoryElements = restaurant?.Categories?.map((_, id) => document.getElementById(`category-${id + 1}`));
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      let next = 0;

      categoryElements?.forEach((element, index) => {
        if (element) {
          const rect = element.getBoundingClientRect();
          const offsetTop = rect.top + window.scrollY;
          if (scrollPosition >= offsetTop) next = index;
        }
      });

      setActiveCategory((prev) => (prev === next ? prev : next));
    }, 120);

    window.addEventListener("scroll", handleScrollThrottled, { passive: true });
    return () => window.removeEventListener("scroll", handleScrollThrottled);
  }, [restaurant]);

  useEffect(() => {
    // ensure active category stays centered in the horizontal list
    const activeCategoryElement = document.querySelector(".category-item.active");
    if (activeCategoryElement && categoryListRef.current) {
      const containerScrollLeft = categoryListRef.current.scrollLeft;
      const containerWidth = categoryListRef.current.clientWidth;
      const elementLeft = activeCategoryElement.offsetLeft;
      const elementWidth = activeCategoryElement.clientWidth;

      if (elementLeft < containerScrollLeft) {
        categoryListRef.current.scrollTo({ left: elementLeft - 100, behavior: "smooth" });
      } else if (elementLeft + elementWidth > containerScrollLeft + containerWidth) {
        categoryListRef.current.scrollTo({ left: elementLeft + elementWidth - containerWidth + 100, behavior: "smooth" });
      } else {
        const centerPosition = elementLeft - containerWidth / 2 + elementWidth / 2;
        categoryListRef.current.scrollTo({ left: centerPosition, behavior: "smooth" });
      }
    }
  }, [activeCategory]);

  // Handlers
  const getCategoryOffset = (categoryId) => {
    const el = document.getElementById(`category-${categoryId}`);
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    return rect.top + window.scrollY - 200;
  };

  const handleCategoryClick = (categoryIndex) => {
    setActiveCategory(categoryIndex);
    const top = getCategoryOffset(categoryIndex + 1);
    window.scrollTo({ top, behavior: "smooth" });
    setCategoryModalVisible(false);

    const activeEl = document.querySelector(`.category-item:nth-child(${categoryIndex + 1})`);
    if (activeEl && categoryListRef.current) {
      const elementLeft = activeEl.offsetLeft;
      const containerWidth = categoryListRef.current.clientWidth;
      const elementWidth = activeEl.clientWidth;
      const centerPosition = elementLeft - containerWidth / 2 + elementWidth / 2;
      categoryListRef.current.scrollTo({ left: centerPosition, behavior: "smooth" });
    }
  };

  const handleOpenAddToCartModal = (item) => {
    if (restaurantStatus?.stop_order) {
      Swal.fire({ icon: "error", title: "Restaurant Too Busy", text: "Online order is currently closed!" });
      return;
    }
    setSelectedItem(item);
    setAddToCartModal(true);
  };

  const handleFastAddToCart = (item) => {
    const hasRequired = (item?.Restaurant_Item_Attributes || []).some((attr) => attr?.Item_Attribute?.requ > 0);
    if (hasRequired) {
      setSelectedItem(item);
      setAddToCartModal(true);
      return;
    }
    const key = `cart${restaurantId}`;
    const next = JSON.parse(localStorage.getItem(key) || "[]");

    const toAdd = {
      Item: item,
      quantity: 1,
      special_instructions: "",
      options: [],
      price: Number(item?.price) || 0,
    };

    const idx = next.findIndex(
      (c) =>
        c?.Item?.id === toAdd.Item.id &&
        c?.special_instructions === toAdd.special_instructions &&
        JSON.stringify(c?.options || []) === JSON.stringify(toAdd.options)
    );

    if (idx !== -1) {
      const qty = (Number(next[idx].quantity) || 1) + 1;
      next[idx].quantity = qty;
      const base = Number(next[idx]?.Item?.price) || 0;
      const mods = (next[idx]?.options || []).reduce((s, o) => s + (Number(o?.priceModifier) || 0), 0);
      next[idx].price = Number(((base + mods) * qty).toFixed(2));
    } else {
      next.push(toAdd);
    }

    localStorage.setItem(key, JSON.stringify(next));
    setChangedCart((v) => !v);
  };

  const handleFastRemoveFromCart = (item) => {
    const key = `cart${restaurantId}`;
    const cart = JSON.parse(localStorage.getItem(key) || "[]");
    const idx = cart.findIndex((c) => c?.Item?.id === item?.id);
    if (idx !== -1) {
      cart[idx].quantity = (Number(cart[idx].quantity) || 1) - 1;
      if (cart[idx].quantity <= 0) {
        cart.splice(idx, 1);
      } else {
        const base = Number(cart[idx]?.Item?.price) || 0;
        const mods = (cart[idx]?.options || []).reduce((s, o) => s + (Number(o?.priceModifier) || 0), 0);
        cart[idx].price = Number(((base + mods) * (Number(cart[idx].quantity) || 1)).toFixed(2));
      }
      localStorage.setItem(key, JSON.stringify(cart));
      setChangedCart((v) => !v);
    }
  };

  const checkItemQuantity = (item) => {
    const cart = JSON.parse(localStorage.getItem(`cart${restaurantId}`) || "[]");
    const found = cart.find((c) => c?.Item?.id === item?.id);
    return found?.quantity > 0 ? found.quantity : false;
  };

  const handleClearSearchInput = () => setSearchValue("");

  const highlightText = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <span key={i} className="highlight">
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  const handleChooseLocation = (id) => {
    setRestaurantId(id);
    setShowChooseRestaurant(false);
    setChangedCart((v) => !v);
  };

  // Locations for modal (prefer API list)
  const locationCards = (locationsFromApi?.length
    ? locationsFromApi
    : [
        { id: 7, title: "Cordova Location", address: "1250 N Germantown Pkwy", city: "Cordova, TN 38016", phone: "(901)-707-8886" },
        { id: 11, title: "Overton Square Location", address: "2110 Madison Ave", city: "Memphis, TN 38104", phone: "(901)-207-6680" },
        { id: 40, title: "小富贵Little Petals Chinese", address: "149 Madison Ave", city: "Memphis, TN 38103", phone: "(901)-628-0086" },
      ]
  ).map((loc, idx) => (
    <div key={idx} className="col-12 col-md-6 col-lg-5">
      <div className="border rounded shadow-sm h-100 p-4 text-center">
        <h5 className="fw-bold mb-3">{loc?.title || loc?.name || `Store #${loc?.id}`}</h5>
        {loc?.address && <p className="mb-2">{loc.address}</p>}
        {loc?.city && <p className="mb-2">{loc.city}</p>}
        {loc?.phone && <p className="mb-3">{loc.phone}</p>}
        <button onClick={() => handleChooseLocation(loc.id)} className="btn btn-primary w-100">
          Order Here
        </button>
      </div>
    </div>
  ));

  // Render
  return (
    <>
      <Header />

      {/* Choose location (dynamic) */}
   <Modal
  show={showChooseRestaurant}
  centered
  size="lg"
  onHide={() => {}} // ❌ disable closing
  backdrop="static" // ❌ clicking outside won’t close
  keyboard={false}  // ❌ pressing Esc won’t close
>
  {/* Header */}
  <Modal.Header
    
    className="border-0"
    style={{
      backgroundColor: "#fff9f7",
      borderBottom: "1px solid rgba(157,42,0,0.15)",
    }}
  >
    <Modal.Title className="fw-bold text-dark">
      Please Choose a Location
    </Modal.Title>
  </Modal.Header>

  {/* Body */}
  <Modal.Body
    className="bg-white text-dark"
    style={{
      borderRadius: "0 0 1rem 1rem",
      backgroundColor: "#fff9f7",
      maxHeight: "80vh",
      overflowY: "auto",
    }}
  >
    <div className="container py-4">
      <h4
        className="text-center fw-bold mb-4"
        style={{ color: "#9d2a00" }}
      >
        Our Locations
      </h4>

      <div className="row justify-content-center gy-4">
        {(locationsFromApi?.length
          ? locationsFromApi
          : [
              {
                id: 7,
                title: "Cordova Location",
                address: "1250 N Germantown Pkwy",
                city: "Cordova, TN 38016",
                phone: "(901)-707-8886",
              },
              {
                id: 11,
                title: "Overton Square Location",
                address: "2110 Madison Ave",
                city: "Memphis, TN 38104",
                phone: "(901)-207-6680",
              },
              {
                id: 40,
      title: "小富贵 Little Petal Chinese(Downtown)",
                address: "149 Madison Ave",
                city: "Memphis, TN 38103",
                phone: "(901)-628-0086",
              },
              {
                id: 73,
      title: "小富贵 Little Petal Chinese 2(East Memphis)",
                address: "5167 Poplar Ave",
                city: "Memphis, TN 38117",
                phone: "(901)-668-9388",
              },
            ]
        ).map((loc, idx) => {
          const isSelected = restaurantId === loc.id;
          return (
            <div key={idx} className="col-12 col-md-6 col-lg-6">
              <div
                className={`p-4 border rounded-4 shadow-sm h-100 text-start ${
                  isSelected ? "border-2" : ""
                }`}
                style={{
                  borderColor: isSelected
                    ? "#9d2a00"
                    : "rgba(157, 42, 0, 0.15)",
                  background: isSelected ? "#fff3ef" : "#ffffffff",
                  boxShadow: isSelected
                    ? "0 4px 10px rgba(157,42,0,0.25)"
                    : "0 1px 4px rgba(0,0,0,0.05)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow =
                    "0 3px 8px rgba(157,42,0,0.2)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow = isSelected
                    ? "0 4px 10px rgba(157,42,0,0.25)"
                    : "0 1px 4px rgba(0,0,0,0.05)")
                }
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h5 className="fw-bold text-dark mb-0">
                    {loc?.title || loc?.name || `Store #${loc?.id}`}
                  </h5>
                  {isSelected && (
                    <span
                      className="badge text-white"
                      style={{
                        backgroundColor: "#9d2a00",
                        fontSize: "0.8rem",
                      }}
                    >
                      Selected
                    </span>
                  )}
                </div>

                <div className="mb-3">
                  {loc?.address && (
                    <p className="mb-1 fw-medium text-dark">
                      {loc.address}
                    </p>
                  )}
                  {loc?.city && (
                    <p
                      className="mb-1 text-muted"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {loc.city}
                    </p>
                  )}
                  {loc?.phone && (
                    <a
                      href={`tel:${loc.phone.replace(/[^0-9]/g, "")}`}
                      className="fw-semibold text-decoration-none"
                      style={{ color: "#9d2a00" }}
                    >
                      {loc.phone}
                    </a>
                  )}
                </div>

                <button
                  onClick={() => handleChooseLocation(loc.id)}
                  className="btn fw-semibold w-100 py-2"
                  style={{
                    backgroundColor: "#9d2a00",
                    color: "#fff",
                    borderRadius: 12,
                    fontSize: "1rem",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#b83500")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#9d2a00")
                  }
                >
                  {isSelected ? "Continue Ordering" : "Order Here"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </Modal.Body>
</Modal>


      <div className="page-content bg-white mt-2">
        <section className="content-inner">
          {/* Selected location quick note */}
{restaurantId && (
  <div
    className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-2 mx-auto mt-3 mb-3 px-3 py-2"
    style={{
      maxWidth: 700,
      backgroundColor: "#fff9f7",
      border: "1px solid rgba(157, 42, 0, 0.2)",
      borderRadius: 12,
      boxShadow: "0 2px 4px rgba(157,42,0,0.08)",
    }}
  >
    {/* Left side: restaurant name + address */}
    <div className="text-center text-sm-start">
      <p
        className="fw-bold mb-1"
        style={{ color: "#9d2a00", fontSize: "1rem" }}
      >
        {restaurantId === 7
          ? "Petals of a Peony - Cordova"
          : restaurantId === 11
          ? "Petals of a Peony - Overton Square"
          : restaurantId === 40
          ? "小富贵 Little Petal Chinese - Downtown"
          : restaurantId === 73
          ? "小富贵 Little Petal Chinese 2 - East Memphis"
          : "Selected Store"}
      </p>

      <p
        className="mb-0 text-dark fw-medium"
        style={{
          fontSize: "0.9rem",
          lineHeight: "1.4",
          color: "#333",
        }}
      >
        {restaurantId === 7
          ? "1250 N Germantown Pkwy, Cordova, TN 38016"
          : restaurantId === 11
          ? "2110 Madison Ave, Memphis, TN 38104"
          : restaurantId === 40
          ? "149 Madison Ave, Memphis, TN 38103"
          : restaurantId === 73
          ? "5167 Poplar Ave, Memphis, TN 38117"
          : "Current Location"}
      </p>
    </div>

    {/* Right side: Change button */}
    <button
      className="fw-semibold mt-2 mt-sm-0 px-3 py-2 border-0"
      onClick={() => setShowChooseRestaurant(true)}
      style={{
        backgroundColor: "#9d2a00",
        color: "#fff",
        borderRadius: 8,
        fontSize: "0.9rem",
        whiteSpace: "nowrap",
        boxShadow: "0 2px 6px rgba(157,42,0,0.2)",
        transition: "background-color 0.2s ease",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.backgroundColor = "#b83500")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.backgroundColor = "#9d2a00")
      }
    >
      Change Location
    </button>
  </div>
)}



          {/* Category bar + search */}
          <div className="category-menu-wrapper">
            <div className="category-container container">
              <div className="d-flex align-items-center">
                <i className="fa fa-bars category-icon" onClick={() => setCategoryModalVisible(true)} />
                <div className="category-list-wrapper" ref={categoryListRef}>
                  <div className="category-list">
                    {(restaurant?.Categories || []).map((cat, index) => (
                      <span
                        key={cat.id}
                        className={`category-item ${activeCategory === index ? "active" : ""}`}
                        onClick={() => handleCategoryClick(index)}
                      >
                        {cat.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="search-input-container">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <FontAwesomeIcon icon={faTimes} className="clear-icon" onClick={handleClearSearchInput} />
              </div>
            </div>
          </div>

          <div className="mt-4" />

          {/* Items */}
          <div className="container" style={{ marginBottom: 100 }}>
            <div className="row">
              {(filteredCategories || [])
                .filter((c) => c.id !== 105 && c.id !== 104) // preserve your existing filter
                .map(({ id: category_id, name, Items }, idx) => {
                  // sort by number in chinese_name if present
                  const sorted = [...(Items || [])].sort((a, b) => {
                    const numA = parseInt(a?.chinese_name);
                    const numB = parseInt(b?.chinese_name);
                    if (Number.isNaN(numA) || Number.isNaN(numB)) return 0;
                    return numA - numB;
                  });

                  return (
                    <div key={category_id || idx} id={`category-${idx + 1}`} className="mt-3">
                      <h2 className="text-black fw-bold">{name}</h2>
                      <div className="row">
                        {sorted.map(({ id, name, description, price, image_url, Restaurant_Item_Attributes, chinese_name, hot_item, sold_out }) => (
                          <div
                            key={id}
                            className={`col-md-6 mb-3 ${sold_out ? "opacity-75" : "cursor-pointer"}`}
                            onClick={
                              sold_out
                                ? undefined
                                : () =>
                                    handleOpenAddToCartModal({
                                      id,
                                      name,
                                      description,
                                      price,
                                      image_url,
                                      Restaurant_Item_Attributes,
                                      chinese_name,
                                      category_id,
                                      hot_item,
                                    })
                            }
                          >
                            <div className="item-card d-flex flex-row position-relative rounded overflow-hidden border">
                              <div className="item-image-container">
                                {image_url && (
                                  <img
                                    src={image_url}
                                    alt={name}
                                    className="item-image"
                                    loading="lazy"
                                    style={{ width: 160, height: 120, objectFit: "cover" }}
                                  />
                                )}
                              </div>

                              <div className="item-details flex-grow-1 p-2">
                                <h5 className="item-title text-black">
                                  {language === "cn" ? (chinese_name || name) : highlightText(name, debouncedSearch)}
                                </h5>
                                <p className="item-description text-muted text-black mb-2" style={{ overflow: "hidden" }}>
                                  {description && description !== "null" ? (description.length > 27 ? `${description.slice(0, 27)}...` : description) : ""}
                                </p>

                                <div className="d-flex align-items-center justify-content-between">
                                  <p className="item-price mb-0">${price}</p>
                                  <div className="d-flex align-items-center">
                                    {checkItemQuantity({ id }) && (
                                      <>
                                        <FontAwesomeIcon
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleFastRemoveFromCart({ id });
                                          }}
                                          icon={faMinusCircle}
                                          fontSize={24}
                                          style={{ color: "#9d2a00" }}
                                        />
                                        <span className="mx-2 fw-bold text-black">{checkItemQuantity({ id })}</span>
                                      </>
                                    )}
                                    {!sold_out && (
                                      <FontAwesomeIcon
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleFastAddToCart({
                                            id,
                                            name,
                                            description,
                                            price,
                                            image_url,
                                            Restaurant_Item_Attributes,
                                            chinese_name,
                                            category_id,
                                            hot_item,
                                          });
                                        }}
                                        icon={faPlusCircle}
                                        fontSize={24}
                                        style={{ color: "#9d2a00" }}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {sold_out && (
                                <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center">
                                  <span className="text-white fw-bold">Sold Out</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Floating footer -> opens Cart Drawer */}
            {((currentCart?.length || 0) > 0) && (
              <div className="row fixed-bottom text-center custom-footer bg-white text-black">
                <div className="custom-footer-section px-4 p-1" onClick={() => setOpenDrawer(true)} style={{ cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faShoppingBag} fontSize={24} className={`text-white ${shake ? "swing" : ""}`} />
                  <p className="btn text-white fw-bold mb-0">View Cart</p>
                  <span className="badge badge-pill custom-badge">{calculateCartItems()}</span>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Modals */}
        <AddToCartModal modal={addModal} item={selectedItem} setModal={setAddToCartModal} />

        <CategoryModal show={categoryModalVisible} onClose={() => setCategoryModalVisible(false)}>
          <div className="category-modal-content">
            {(restaurant?.Categories || []).map((cat, index) => (
              <div
                key={cat.id}
                className={`category-modal-item ${activeCategory === index ? "active" : "text-black"}`}
                onClick={() => handleCategoryClick(index)}
              >
                {cat.name}
              </div>
            ))}
          </div>
        </CategoryModal>
      </div>

      {/* Drawer (mobile: bottom; desktop: right) */}
      <CartDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        restaurantId={restaurantId}
        changedCart={changedCart}
        setChangedCart={setChangedCart}
        language={language}
      />
    </>
  );
};

export default OnlineOrder;
